import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Checkbox, Spin } from "antd";
import { Button, ButtonLink } from "../../misc/button/Button";
import { AuthContext } from "../../context/AuthProviderWrapper";
import { DeclarationContext } from "../../context/DeclarationWrapper";
import { useMediaQuery } from "react-responsive";
import { renderCheckboxOrHidden } from "../../helper-function/helperFunction";
import EditDeclarationModal from "../declarationDetail-CRUD/EditDeclarationModal";
import DsnFileUpload from "../../misc/DsnFileUpload";

function DeclarationSummaryPage() {
  const { user, activeCompany } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [isEditableFlag, setIsEditableFlag] = useState(false);
  const { declaration, updateDeclaration, dsnFile, isAttestation } = useContext(DeclarationContext);
  const isSmallScreen = useMediaQuery({ maxWidth: 990 });
  const [conditionCheckbox, setConditionCheckbox] = useState(false);

  // Détecter le type de déclaration
  const isAnnual = declaration?.isAnnual || false;
  const isSemiAnnual = declaration?.isSemiAnnual || false;

  useEffect(() => {
    const condition = renderCheckboxOrHidden(activeCompany, declaration);
    setConditionCheckbox(condition);
  }, [activeCompany, declaration]);

  useEffect(() => {
    updateDeclaration((prev) => ({
      ...prev,
      membershipCode: activeCompany.membershipCode,
      // Préserver isAnnual s'il était déjà défini
      isAnnual: prev?.isAnnual || false,
    }));
  }, [activeCompany]);

  const checkIfEditable = (declaration) => {
    if (!declaration?.id || (declaration?.payed === "no" && user.isAdmin)) {
      return true;
    }
    return false;
  };

  const handleEdit = (record) => {
    const flag = checkIfEditable(record);
    setIsEditableFlag(flag);
    setEditModalVisible(true);
  };

  const handleEditModalCancel = () => {
    setEditModalVisible(false);
    setIsEditableFlag(false);
  };

  /**
   * Formate la période de déclaration selon le type
   * @param {number} inputedDdate - Timestamp de la date
   * @param {boolean} isAnnual - Déclaration annuelle
   * @param {boolean} isSemiAnnual - Déclaration semestrielle
   * @param {number} semester - Numéro du semestre (1 ou 2)
   * @returns {string} Période formatée
   */
  const formatDeclarationPeriod = (inputedDdate, isAnnual, isSemiAnnual, semester) => {
    if (!inputedDdate) return '';

    const date = new Date(inputedDdate * 1000);
    const year = date.getFullYear();

    if (isAnnual) {
      return year.toString();
    }

    if (isSemiAnnual && semester) {
      const semesterLabel = semester === 1 ? '1er' : '2ème';
      return `${semesterLabel} semestre ${year}`;
    }

    return new Intl.DateTimeFormat('fr-FR', {
      month: 'long',
      year: 'numeric'
    }).format(date);
  };

  /**
   * Retourne le type de déclaration pour l'affichage
   */
  const getDeclarationType = () => {
    if (isAnnual) return 'régularisation annuelle';
    if (isSemiAnnual) return 'déclaration semestrielle';
    return 'déclaration';
  };

  return (
    <>
      <Spin spinning={isLoading} fullscreen />
      <div className="block-right">
        <div className="declaration-summary-page">
        <h2>
          Récapitulatif de votre {getDeclarationType()}
          {declaration?.inputedDdate > 0
            ? ' pour ' + formatDeclarationPeriod(declaration.inputedDdate, isAnnual, isSemiAnnual, declaration.semester)
            : ''}
        </h2>
          <div className="info-salary">
            <div>
              <table>
                <thead>
                  <tr>
                    <th scope="col"></th>
                    <th scope="col">Salaire</th>
                    <th scope="col">Cotisation ({activeCompany?.feeRate || '0,4%'})</th>
                    <th scope="col">TVA</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>Salaire Brut Cadres</th>
                    <td>
                      <div className="input">
                        {declaration?.salaryExecutiveLocale}
                      </div>
                    </td>
                    <td>
                      <div className="input">
                        {declaration?.feeExecutiveLocale}
                      </div>
                    </td>
                    <td>
                      <div className="input">
                        {declaration?.tvaExecutiveLocale}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Salaire Brut ETAM</th>
                    <td>
                      <div className="input">
                        {declaration?.salaryEtamLocale}
                      </div>
                    </td>
                    <td>
                      <div className="input">{declaration?.feeEtamLocale}</div>
                    </td>
                    <td>
                      <div className="input">{declaration?.tvaEtamLocale}</div>
                    </td>
                  </tr>
                  <tr>
                    <th>
                      Salaire Brut Ouvriers{" "}
                      {(activeCompany?.laborUnion === 2 || activeCompany?.laborUnion === 4)
                        ? (declaration?.workForce == null || declaration.workForce <= 10 ? "(opt.)" : "")
                        : (declaration?.workForce > 10 ? "(opt.)" : "")
                      }
                    </th>
                    <td>
                      <div className="input">
                        {declaration?.salaryWorkerLocale}
                      </div>
                    </td>
                    <td>
                      <div className="input">
                        {declaration?.feeWorkerLocale}
                      </div>
                    </td>
                    <td>
                      <div className="input">
                        {declaration?.tvaWorkerLocale}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Date</th>
                    <td>
                      <div className="input">
                        {formatDeclarationPeriod(declaration?.inputedDdate, isAnnual, isSemiAnnual, declaration?.semester)}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Effectifs</th>
                    <td>
                      <div className="input">{declaration?.workForce}</div>
                    </td>
                  </tr>
                  <tr>
                    <th>Montant dû</th>
                    <td>
                      <div className="input">
                        {declaration?.feeAmountLocale}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Condition: user.laborUnion !== 2 && declaration?.workForce > 10 */}
            {conditionCheckbox && (
              <div
                style={{ height: isSmallScreen ? "100px" : "50px" }}
                className="flex-row visible"
              >
                <div style={{ marginTop: "20px" }}>
                  <Checkbox
                    name="agreeOptionalWorker"
                    checked={declaration?.agreeOptionalWorker}
                    disabled={true}
                  >
                    J'ai pris connaissance que la souscription aux Prestations
                    pour les Ouvriers est optionnelle si les effectifs sont
                    supérieurs à 10.
                  </Checkbox>
                </div>
              </div>
            )}

            {/* Affichage et modification du fichier DSN pour les attestations */}
            {isAttestation(declaration) && (
              <DsnFileUpload />
            )}
          </div>
          <div className="button-container">
            <ButtonLink to={isSemiAnnual ? "/semi-annual-declaration" : (isAnnual ? "/annual-declaration" : "/declaration")} children="Retour" />
            <Button
              // onClick={() => handleEdit(declarationData)}
              onClick={() => {
                handleEdit(declaration);
              }}
              children="Modifier"
            />
            <ButtonLink to="/declaration-signature" children="Valider" />

            <EditDeclarationModal
              open={editModalVisible}
              onCancel={handleEditModalCancel}
              record={declaration}
              mode={isEditableFlag ? "edit" : "view"}
              navigateTo="/declaration-summary"
              isPreviewMode={true}
              isAnnual={isAnnual}
              isSemiAnnual={isSemiAnnual}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default DeclarationSummaryPage;
